import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatNumber,
  formatPercentage,
} from "@repo/components/src/utils/numbers";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { PlatformCodeEnum } from "@repo/graphql/gql/graphql";
import { Check, X } from "lucide-react-native";

import ContentPoster from "./contentPoster";
import { isScreenshotReportedDeliverable } from "./helpers";
import React, { useMemo, useState } from "react";
import { formatDateShort, platformLabel } from "../shared/utils";

export type DeliverableItem = {
  id: string;
  workOrder: CampaignAnalyticFragmentFragment["workOrders"][number];
  detail: CampaignAnalyticFragmentFragment["workOrders"][number]["details"][number];
};

type Props = {
  items: DeliverableItem[];
  rightOfUseMonths: number | null | undefined;
  onPress: (item: DeliverableItem) => void;
};

type SortKey =
  | "publishedDesc"
  | "publishedAsc"
  | "reach"
  | "interactions"
  | "views"
  | "comments"
  | "shares";

type PlatformFilter =
  | "all"
  | PlatformCodeEnum.Instagram
  | PlatformCodeEnum.TikTok;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "publishedDesc", label: "Más recientes" },
  { value: "publishedAsc", label: "Más antiguas" },
  { value: "reach", label: "Alcance" },
  { value: "interactions", label: "Interacciones" },
  { value: "views", label: "Views" },
  { value: "comments", label: "Comentarios" },
  { value: "shares", label: "Compartidos" },
];

const publishedTime = (item: DeliverableItem): number => {
  const raw = item.detail.publishedAt ?? item.detail.approvedAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

const sortDeliverables = (
  items: DeliverableItem[],
  key: SortKey,
): DeliverableItem[] => {
  const copy = [...items];
  switch (key) {
    case "publishedDesc":
      return copy.sort((a, b) => publishedTime(b) - publishedTime(a));
    case "publishedAsc":
      return copy.sort((a, b) => publishedTime(a) - publishedTime(b));
    case "reach":
      return copy.sort((a, b) => b.detail.reach - a.detail.reach);
    case "interactions":
      return copy.sort((a, b) => b.detail.interactions - a.detail.interactions);
    case "views":
      return copy.sort((a, b) => b.detail.views - a.detail.views);
    case "comments":
      return copy.sort((a, b) => b.detail.comments - a.detail.comments);
    case "shares":
      return copy.sort((a, b) => b.detail.shares - a.detail.shares);
  }
};

const Deliverables = React.memo(
  ({ items, rightOfUseMonths, onPress }: Props) => {
    const [platform, setPlatform] = useState<PlatformFilter>("all");
    const [sort, setSort] = useState<SortKey>("publishedDesc");

    const igCount = useMemo(
      () =>
        items.filter(
          (it) => it.detail.platformCode === PlatformCodeEnum.Instagram,
        ).length,
      [items],
    );
    const ttCount = useMemo(
      () =>
        items.filter((it) => it.detail.platformCode === PlatformCodeEnum.TikTok)
          .length,
      [items],
    );

    const visible = useMemo(() => {
      const published = items.filter((it) => !!it.detail.publishedAt);
      const filtered =
        platform === "all"
          ? published
          : published.filter((it) => it.detail.platformCode === platform);
      return sortDeliverables(filtered, sort);
    }, [items, platform, sort]);

    return (
      <VStack space="md">
        <HStack className="justify-between items-center flex-wrap" space="md">
          <PlatformToggle
            value={platform}
            onChange={setPlatform}
            igDisabled={igCount === 0}
            ttDisabled={ttCount === 0}
          />
          <HStack space="sm" className="items-center">
            <Text size="xs" className="text-typography-500">
              Ordenar por
            </Text>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              style={{
                border: "1px solid rgb(229,231,235)",
                background: "#fff",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 13,
                color: "rgb(15,15,25)",
                fontWeight: 600,
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </HStack>
        </HStack>

        {!visible.length ? (
          <Text size="sm" className="text-typography-500">
            No hay contenido publicado para esta selección.
          </Text>
        ) : (
          <HStack flexSpace="md">
            {visible.map((item) => (
              <Box
                key={item.id}
                className="w-full sm:w-1/2 md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/6"
              >
                <DeliverableCard
                  item={item}
                  rightOfUseMonths={rightOfUseMonths}
                  onPress={onPress}
                />
              </Box>
            ))}
          </HStack>
        )}
      </VStack>
    );
  },
);

const PlatformToggle = ({
  value,
  onChange,
  igDisabled,
  ttDisabled,
}: {
  value: PlatformFilter;
  onChange: (v: PlatformFilter) => void;
  igDisabled: boolean;
  ttDisabled: boolean;
}) => {
  const options: {
    value: PlatformFilter;
    label: string;
    disabled?: boolean;
  }[] = [
    { value: "all", label: "Todos" },
    {
      value: PlatformCodeEnum.Instagram,
      label: "Instagram",
      disabled: igDisabled,
    },
    { value: PlatformCodeEnum.TikTok, label: "TikTok", disabled: ttDisabled },
  ];
  return (
    <Box className="bg-background-100 rounded-lg p-1">
      <HStack space="xs">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <Pressable
              key={String(opt.value)}
              onPress={() => !opt.disabled && onChange(opt.value)}
              disabled={opt.disabled}
              className={
                active
                  ? "px-3 py-1.5 rounded-md bg-background-0"
                  : "px-3 py-1.5 rounded-md"
              }
            >
              <Text
                size="xs"
                className={
                  opt.disabled
                    ? "text-typography-300 font-semibold"
                    : active
                      ? "text-typography-900 font-semibold"
                      : "text-typography-500 font-semibold"
                }
              >
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </HStack>
    </Box>
  );
};

Deliverables.displayName = "OrganicDeliverables";

type CardProps = {
  item: DeliverableItem;
  rightOfUseMonths: number | null | undefined;
  onPress: (item: DeliverableItem) => void;
};

const DeliverableCard = ({ item, rightOfUseMonths, onPress }: CardProps) => {
  const { workOrder, detail } = item;
  const isSelfReported = isScreenshotReportedDeliverable(detail);
  const thumbnailUrl = isSelfReported
    ? (detail.contentFile?.url ?? detail.thumbnail?.url)
    : detail.thumbnail?.url;
  const avatar = workOrder.avatar;
  const platform = platformLabel(detail.platformCode);
  const publishedAt =
    formatDateShort(detail.publishedAt ?? detail.approvedAt) ?? "—";
  const er = detail.engagementRate ?? 0;
  const erHigh = er >= 0.08;
  const hasRights = !!rightOfUseMonths;
  return (
    <Pressable
      onPress={() => onPress(item)}
      className="border border-outline-100 rounded-2xl overflow-hidden h-full"
    >
      <Box className="relative aspect-[9/12] bg-background-100">
        <ContentPoster url={thumbnailUrl} alt={detail.postName} />
        <Box className="absolute top-2 left-2">
          <Badge action="muted" rounded="full" size="sm">
            <BadgeText>{platform}</BadgeText>
          </Badge>
        </Box>
        <Box className="absolute top-2 right-2">
          <Badge variant="solid" action="muted" rounded="md" size="sm">
            <BadgeText>{detail.postName}</BadgeText>
          </Badge>
        </Box>
        <Box className="absolute left-3 right-3 bottom-3">
          <VStack space="xs">
            <HStack space="xs" className="items-center">
              <Avatar size="xs">
                <AvatarFallbackText>{workOrder.fullName}</AvatarFallbackText>
                <AvatarImage source={{ uri: avatar?.url }} />
              </Avatar>
              <VStack className="flex-shrink">
                <Text size="xs" className="text-white font-semibold">
                  {workOrder.fullName}
                </Text>
                <Text size="2xs" className="text-white opacity-80">
                  Publicado el {publishedAt}
                </Text>
              </VStack>
            </HStack>
            <HStack space="sm" className="items-center justify-between">
              <Text size="xs" className="text-white font-semibold">
                Reach {formatNumber(detail.reach)}
              </Text>
              <Text size="xs" className="text-white font-semibold">
                Views {formatNumber(detail.views)}
              </Text>
              <Badge
                action={erHigh ? "success" : "muted"}
                variant="solid"
                rounded="md"
                size="sm"
              >
                <BadgeText>ER {formatPercentage(er)}</BadgeText>
              </Badge>
            </HStack>
          </VStack>
        </Box>
      </Box>
      <HStack className="px-3 py-2" space="xs">
        {/* Self-reported deliverables report replies and link taps; they have
            no comments or saves, so showing those would just be two
            permanent zeros. */}
        {isSelfReported ? (
          <>
            <Stat label="Likes" value={detail.likes} />
            <Stat label="Replies" value={detail.replyCount} />
            <Stat label="Shares" value={detail.shares} />
            <Stat label="Clicks" value={detail.linkClicks} />
          </>
        ) : (
          <>
            <Stat label="Likes" value={detail.likes} />
            <Stat label="Comments" value={detail.comments} />
            <Stat label="Shares" value={detail.shares} />
            <Stat label="Saves" value={detail.saves} />
          </>
        )}
      </HStack>
      {isSelfReported ? (
        <Box className="px-3 pb-2">
          <Badge action="muted" variant="outline" rounded="md" size="sm">
            <BadgeText>Story – autoreportado y verificado por Montu</BadgeText>
          </Badge>
        </Box>
      ) : null}
      <HStack
        className="px-3 py-2 border-t border-outline-100 items-center"
        space="xs"
      >
        <Icon
          as={hasRights ? Check : X}
          size="xs"
          className={hasRights ? "text-success-600" : "text-typography-400"}
        />
        <Text size="xs" className="text-typography-500">
          {hasRights
            ? `Derechos de uso · ${rightOfUseMonths} meses`
            : "Sin derechos de uso"}
        </Text>
      </HStack>
    </Pressable>
  );
};

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box className="flex-1 bg-background-50 border border-outline-100 rounded-md py-1.5 items-center">
    <Text size="xs" className="text-typography-900 font-semibold">
      {formatNumber(value)}
    </Text>
    <Text size="2xs" className="text-typography-400 uppercase">
      {label}
    </Text>
  </Box>
);

export default Deliverables;
